import type { Vector3 } from 'three/webgpu';
import { G0 } from '../universe/HollowDef';

/**
 * Minimal HUD: a gravity dial (direction of "down" as seen on screen, strength
 * in g) and a location line. Updated a few times per second.
 */
export class Hud {
  private readonly arrow = document.getElementById('gravity-arrow')!;
  private readonly gText = document.getElementById('gravity-text')!;
  private readonly loc = document.getElementById('location-text')!;
  private readonly markerRoot = document.getElementById('markers')!;
  private readonly markerEls: HTMLDivElement[] = [];
  private last = 0;

  /** gravityView: gravity vector in camera space (x right, y up, z back). */
  update(gravityView: Vector3, location: string, mode: string): void {
    const now = performance.now();
    if (now - this.last < 100) return;
    this.last = now;
    const g = gravityView.length();
    const sx = gravityView.x;
    const sy = -gravityView.y;
    const planar = Math.hypot(sx, sy);
    const angle = Math.atan2(-sx, sy);
    this.arrow.style.setProperty('--g-angle', `${angle}rad`);
    this.arrow.style.setProperty('--g-len', g > 1e-3 ? `${Math.max(0.08, planar / g)}` : '0');
    const gs = g / G0;
    this.gText.textContent = `${gs < 0.01 ? gs.toFixed(3) : gs.toFixed(2)} g${mode === 'walk' ? '' : ` · ${mode}`}`;
    this.loc.textContent = location;
  }

  /**
   * Waypoint markers, every frame. x, y: view position (-1..1, beyond that off screen; behind
   * the camera, a direction scaled past the edge). Off-screen ones are pinned to the edge.
   */
  markers(list: { x: number; y: number; label: string }[]): void {
    while (this.markerEls.length < list.length) {
      const el = document.createElement('div');
      el.className = 'marker';
      this.markerRoot.appendChild(el);
      this.markerEls.push(el);
    }
    this.markerEls.forEach((el, i) => {
      const m = list[i];
      if (!m) {
        el.style.display = 'none';
        return;
      }
      let { x, y } = m;
      const edge = Math.max(Math.abs(x) / 0.92, Math.abs(y) / 0.85);
      if (edge > 1) {
        x /= edge;
        y /= edge;
      }
      el.style.display = '';
      el.classList.toggle('edge', edge > 1);
      el.style.left = `${((x + 1) / 2) * 100}%`;
      el.style.top = `${((1 - y) / 2) * 100}%`;
      if (el.textContent !== m.label) el.textContent = m.label;
    });
  }
}
